import { useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Camera, Upload, X, Loader2, Image as ImageIcon } from 'lucide-react';

// Image compression utility
const compressImage = (file, maxWidth = 1200, quality = 0.8) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let { width, height } = img;
        
        // Calculate new dimensions
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob(
          (blob) => {
            resolve(new File([blob], file.name, { type: 'image/jpeg' }));
          },
          'image/jpeg',
          quality
        );
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
};

export const ImageUpload = ({ 
  onUploadComplete, 
  uploadType = 'general',
  currentImage = null,
  className = '',
  aspectRatio = 'auto' // 'auto' | 'square' | '4/3'
}) => {
  const { api } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(currentImage);
  const [compressionInfo, setCompressionInfo] = useState(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please select a JPEG, PNG, WebP, or GIF image');
      return;
    }

    // Validate initial file size (10MB max before compression)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be less than 10MB');
      return;
    }

    setUploading(true);
    
    try {
      // Show preview immediately
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target.result);
      reader.readAsDataURL(file);

      // Compress image for faster loading
      const originalSize = file.size;
      let fileToUpload = file;
      
      // Compress if larger than 500KB
      if (file.size > 500 * 1024) {
        fileToUpload = await compressImage(file, 1200, 0.85);
        const compressionRatio = ((1 - fileToUpload.size / originalSize) * 100).toFixed(0);
        setCompressionInfo({
          original: (originalSize / 1024).toFixed(0),
          compressed: (fileToUpload.size / 1024).toFixed(0),
          ratio: compressionRatio
        });
      }

      await uploadFile(fileToUpload);
    } catch (error) {
      console.error('Processing error:', error);
      toast.error('Failed to process image');
      setPreview(currentImage);
    }
  };

  const uploadFile = async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      let endpoint = '/upload/image';
      if (uploadType === 'profile') {
        endpoint = '/upload/profile-picture';
      } else if (uploadType === 'listing') {
        endpoint = '/upload/listing-image';
      }

      const response = await api().post(endpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Store only the relative path - resolves at display time
      const imageUrl = response.data.image_url;
      // For preview, construct full URL
      const previewUrl = `${process.env.REACT_APP_BACKEND_URL}${imageUrl}`;
      setPreview(previewUrl);
      
      if (onUploadComplete) {
        // Pass relative path to parent for storage
        onUploadComplete(imageUrl, response.data.image_id);
      }
      
      toast.success('Image uploaded successfully');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload image');
      setPreview(currentImage);
    } finally {
      setUploading(false);
      setCompressionInfo(null);
    }
  };

  const clearImage = () => {
    setPreview(null);
    setCompressionInfo(null);
    if (onUploadComplete) {
      onUploadComplete(null, null);
    }
  };

  const aspectClasses = {
    'auto': 'h-48',
    'square': 'aspect-square',
    '4/3': 'aspect-[4/3]'
  };

  return (
    <div className={`relative ${className}`}>
      {preview ? (
        <div className="relative">
          <img 
            src={preview} 
            alt="Preview" 
            className={`w-full ${aspectClasses[aspectRatio]} object-cover rounded-xl border border-white/10`}
          />
          {!uploading && (
            <button
              onClick={clearImage}
              className="absolute top-2 right-2 p-1.5 bg-black/70 rounded-full hover:bg-red-600 transition-colors"
              aria-label="Remove image"
            >
              <X className="w-4 h-4 text-white" />
            </button>
          )}
          {uploading && (
            <div className="absolute inset-0 bg-black/60 rounded-xl flex flex-col items-center justify-center gap-3">
              <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
              <span className="text-sm text-white">Uploading...</span>
              {compressionInfo && (
                <span className="text-xs text-zinc-400">
                  Compressed {compressionInfo.original}KB → {compressionInfo.compressed}KB ({compressionInfo.ratio}% smaller)
                </span>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className={`border-2 border-dashed border-zinc-700 rounded-xl p-8 text-center hover:border-blue-500/50 transition-colors bg-zinc-900/30 ${aspectClasses[aspectRatio]} flex flex-col items-center justify-center`}>
          <div className="flex flex-col items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-blue-500/10 flex items-center justify-center">
              <ImageIcon className="w-7 h-7 text-blue-400" />
            </div>
            
            <div>
              <p className="text-white font-medium mb-1">Upload Image</p>
              <p className="text-zinc-500 text-sm">JPEG, PNG, WebP or GIF • Max 10MB</p>
              <p className="text-zinc-600 text-xs mt-1">Auto-compressed for fast loading</p>
            </div>

            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="border-zinc-600 hover:border-blue-500 hover:bg-blue-500/10"
              >
                <Upload className="w-4 h-4 mr-2" />
                Gallery
              </Button>

              <Button
                type="button"
                size="sm"
                onClick={() => cameraInputRef.current?.click()}
                disabled={uploading}
                className="bg-blue-600 hover:bg-blue-500"
              >
                <Camera className="w-4 h-4 mr-2" />
                Take Photo
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Hidden file inputs */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        onChange={handleFileSelect}
        className="hidden"
        data-testid="gallery-input"
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileSelect}
        className="hidden"
        data-testid="camera-input"
      />

      {/* Change buttons when image exists */}
      {preview && !uploading && (
        <div className="flex gap-2 mt-3">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            className="flex-1 border-zinc-700 hover:border-blue-500"
          >
            <Upload className="w-4 h-4 mr-2" />
            Change
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() => cameraInputRef.current?.click()}
            className="flex-1 bg-blue-600 hover:bg-blue-500"
          >
            <Camera className="w-4 h-4 mr-2" />
            Retake
          </Button>
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
